import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { Cookies } from 'react-cookie';

import { getConfig as nextConfig } from '@belong/configs/next/config';
import type { IPage, IHeaderMenu, IFooterMenu } from '@belong/types';
import { PLATFORM_TYPE } from '@belong/types';
import { COOKIES } from '@belong/constants';
import { getNativeAppCookie } from '@belong/cookies';
import { parsePlatformConfig } from '@belong/env';
import { FeaturesManager, IFeatureToggles, getFeatureToggles } from '@belong/feature-toggles';

import { logger } from '@belong/logging/logger';
import { getCorrelationId } from '@belong/user';
import { removeObjectEmptyProps } from '@belong/type-utils';
import { urls as EXTERNAL_ROUTES, withBasePath } from '@belong/url-utils';
import { getWebsiteConfig } from '@belong/contentful';
import { checkAuthenticationCookies } from '@belong/providers/authentication';
import { InstantSearchServerState } from 'react-instantsearch';
import { getAppPropsWithServerContext } from './utils';

export interface IServerSidePageProps extends ICommonServerSideProps {
  pageData?: IPage;
}
export interface ICommonServerSideProps {
  cookie: string;
  correlationId: string;
  features: IFeatureToggles;
  host: string;
  isBlue: boolean;
  statusCode: number;
  isLoggedIn: boolean;
  headerMenuContent?: IHeaderMenu;
  footerMenuContent?: IFooterMenu;
  pageUrl: string;
  searchServerUrl?: string;
  serverState?: InstantSearchServerState;
}

export const getCommonServerSideProps = async (
  context: GetServerSidePropsContext,
  requireAuthentication = true
): Promise<GetServerSidePropsResult<ICommonServerSideProps>> => {
  const correlationId = getCorrelationId(context);
  const platformConfig = parsePlatformConfig(getNativeAppCookie(context.req?.headers.cookie));
  const isNativeApp = platformConfig.platform !== PLATFORM_TYPE.BROWSER;

  const parsedCookie = new Cookies(context.req?.headers.cookie);
  const contentfulEnvironment = parsedCookie.get(COOKIES.CONTENTFUL_SETTINGS)?.environment;
  const appProps = getAppPropsWithServerContext(context);
  const [{ headerMenuContent, footerMenuContent }, features] = await Promise.all([
    getWebsiteConfig(contentfulEnvironment),
    getFeatureToggles(
      nextConfig().publicRuntimeConfig.featureTogglesApiUrl,
      context?.req?.headers?.cookie,
      appProps.isBlue,
      correlationId
    )
  ]);

  FeaturesManager.init(correlationId, features);

  const protocol = nextConfig().publicRuntimeConfig.env.isDevelopment ? 'http' : 'https';
  const searchServerUrl = `${protocol}://${context.req.headers.host}${withBasePath(context.resolvedUrl)}`;

  const { isLoggedIn } = checkAuthenticationCookies(context.req.cookies, isNativeApp);
  const pageProps = {
    ...appProps,
    isLoggedIn,
    headerMenuContent: removeObjectEmptyProps<IHeaderMenu>(headerMenuContent),
    footerMenuContent: removeObjectEmptyProps<IFooterMenu>(footerMenuContent),
    features,
    correlationId,
    pageUrl: context.resolvedUrl,
    searchServerUrl
  };
  if (!isLoggedIn && requireAuthentication) {
    logger.info(correlationId, 'Common-server-side-props-func: Not authenticated, redirecting to login');
    const loginCallbackUrl = withBasePath(context.resolvedUrl);
    const redirectUrl = `${EXTERNAL_ROUTES.LOGIN}?redirect=${loginCallbackUrl}`;

    return {
      redirect: {
        basePath: false,
        destination: redirectUrl,
        permanent: false
      },
      props: pageProps
    };
  }

  return { props: pageProps };
};
